
import { useState, useEffect } from "react";
import { 
  LineChart, 
  BarChart, 
  PieChart, 
  Lock, 
  Zap, 
  Settings, 
  FileText, 
  Layers, 
  FileSpreadsheet, 
  Users,
  Brain,
  Download
} from "lucide-react";

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}

function Feature({ icon, title, description, delay = 0 }: FeatureProps) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div 
      className={`glass-card p-6 transition-all duration-700 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
    >
      <div className="h-12 w-12 bg-primary-50 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
}

export function Features() {
  return (
    <section id="features" className="py-20 bg-gradient-to-b from-blue-50/50 to-white dark:from-slate-950 dark:to-slate-950 relative">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Powerful Analytics for Better Decisions
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Everything you need to understand your data and drive better business outcomes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Feature
            icon={<Lock className="h-6 w-6" />}
            title="Secure & Compliant"
            description="SOC2 and HIPAA compliant data management with enterprise-grade security protocols."
            delay={100}
          />
          
          <Feature
            icon={<LineChart className="h-6 w-6" />}
            title="Interactive Charts"
            description="Beautiful, responsive visualizations that bring your data to life with real-time updates."
            delay={200}
          />
          
          <Feature
            icon={<Settings className="h-6 w-6" />}
            title="Customizable Dashboards"
            description="Build and customize dashboards that tell your unique data story."
            delay={300}
          />
          
          <Feature
            icon={<Layers className="h-6 w-6" />}
            title="Dashboard Templates"
            description="Pre-built templates for CEO, COO, CMO, and CFO roles to get started instantly."
            delay={400}
          />
          
          <Feature
            icon={<Zap className="h-6 w-6" />}
            title="Integration Ecosystem"
            description="Connect with all your favorite tools including Stripe, QuickBooks, Google Analytics, and more."
            delay={500}
          />
          
          <Feature
            icon={<FileSpreadsheet className="h-6 w-6" />}
            title="Import Your Data"
            description="Upload CSV and TSV files to build custom dashboards with your existing data."
            delay={600}
          />
          
          <Feature
            icon={<Users className="h-6 w-6" />}
            title="Multi-tenant Architecture"
            description="Securely separate data across accounts with granular access controls for teams."
            delay={700}
          />
          
          <Feature
            icon={<Brain className="h-6 w-6" />}
            title="AI-Powered Insights"
            description="Leverage OpenAI to unlock deeper insights and get intelligent recommendations."
            delay={800}
          />
          
          <Feature
            icon={<Download className="h-6 w-6" />}
            title="Export & Share"
            description="Download data and share dashboards with stakeholders to keep everyone informed."
            delay={900}
          />
        </div>
      </div>
    </section>
  );
}
